import React, { useState, useEffect, useContext } from "react";
import { withApollo } from "react-apollo";
import { withStyles } from "@material-ui/core";
import { withOktaAuth } from "@okta/okta-react";
import { NoAccessScreen, LoadingIndicator } from "../../Component";
import { recordTypes } from "../../API/config";
import {
  BrowserRouter as Router,
  Switch,
  Route,
  Redirect,
  useRouteMatch,
  useLocation,
} from "react-router-dom";
import {
  getEntity,
  updateEntity,
  updateConfidentiality,
  exportEntity,
} from "../../API/Entity";
import {
  createCheckRequest,
  referToDealTeam,
  referBackFromDealTeam,
} from "../../API/Check";
import { getListValues } from "../../API/List";
import ModalDialogContext from "../../Context/ModalDialog/ModalDialogContext";
import {
  PageHeader,
  RecordSideBar,
  BasicInfo,
  KYC,
  AML,
  AllocateRequest,
  FinalizeScore,
  ApproveRequest,
  BlockUnblockRequest,
  OrgStructure,
  AugmentRecordDialog,
  Audit,
  Comments,
  Financial,
  ExportSelectorDialog,
} from "./Component";

import RelatedEntities from "../RelatedEntities";
import styles from "./styles";
import { getDependentAttributeChanges } from "./Component/changeHandler";
import { getAttributeValue } from "../../Utilities/Functions";
import withUserDetails from "../../HOC/withUserDetails";
import { useTranslation } from "react-i18next";
import { CancelChecks } from "../ChecksRequest/Component";
import DocumentRequests from "../DocumentRequests";

const RecordView = (props) => {
  const { hasRecordPermission, hasPermission } = props;
  const { t } = useTranslation();

  const {
    showMessage,
    showErrorMessage,
    showLoadingMessage,
    showConfirmation,
  } = useContext(ModalDialogContext);
  const match = useRouteMatch();
  const queryParams = new URLSearchParams(useLocation().search);

  const [urlBase, setUrlBase] = useState();
  const [entityId, setEntityId] = useState();
  const [entity, setEntity] = useState();
  const [load, setLoad] = useState(true);
  const [isLoaded, setIsLoaded] = useState(false);
  const [loadInBackground, setLoadInBackground] = useState(false);
  const [canView, setCanView] = useState();
  const [saving, setSaving] = useState(false);
  const [openAllocateDialog, setOpenAllocateDialog] = useState(false);
  const [openFinalizeDialog, setOpenFinalizeDialog] = useState(false);
  const [openApproveDialog, setOpenApproveDialog] = useState(false);
  const [openBlockDialog, setOpenBlockDialog] = useState(false);
  const [openScoreComments, setOpenScoreComments] = useState(false);
  const [canUpdate, setCanUpdate] = useState();
  const [canUpdateKycFields, setCanUpdateKycFields] = useState();
  const [canUpdateAmlFields, setCanUpdateAmlFields] = useState();
  const [canFinalizeAMLWithRisk, setCanFinalizeAMLWithRisk] = useState();

  const [scoreData, setScoreData] = useState({});
  const [currentRequest, setCurrentRequest] = useState("");
  const [changedAttrIds, setChangedAttrIds] = useState([]);
  const [listOptions, setListOptions] = useState();
  const [basicAttributes, setBasicAttributes] = useState([]);
  const [financialAttributes, setFinancialAttributes] = useState([]);
  const [kycAttributes, setKycAttributes] = useState([]);
  const [amlAttributes, setAmlAttributes] = useState([]);
  const [openCancelCheckDialog, setOpenCancelCheckDialog] = useState();
  const [showAdditionalSources, setShowAdditionalSources] = useState(false);
  const [showExportOptions, setShowExportOptions] = useState(false);

  useEffect(() => {
    const getListOptions = async (listId, listType) => {
      const listValues = [];
      try {
        const result = await getListValues({
          listId,
          listName: null,
          listType,
        });
        const { data = [] } = result;
        for (let lv of data) {
          if (listType === "RL" || listType === "EL") {
            listValues.push({
              value: lv.code,
              label: lv.value,
              equivalent: lv.equivalent,
            });
          } else {
            listValues.push({ value: lv.code, label: lv.value });
          }
        }
      } catch (e) {
        console.log("error in fetching list " + e);
      }
      return listValues;
    };

    const loadListOptions = async (attributes) => {
      try {
        const listsToLoad = [];
        for (let a of attributes) {
          if (
            a.list_id ||
            a.data_type === "CL" ||
            a.data_type === "RL" ||
            (a.data_type === "EL" &&
              (!listOptions || !listOptions[a.list_id || a.data_type]))
          ) {
            if (listOptions && listOptions[a.list_id || a.data_type]) {
              continue;
            }
            const e = listsToLoad.find(
              (l) => l.listId === a.list_id && l.dataType === a.data_type,
            );
            if (!e) {
              listsToLoad.push({ listId: a.list_id, dataType: a.data_type });
            }
          }
        }
        listsToLoad.map(async (l) => {
          const vals = await getListOptions(l.listId, l.dataType);
          setListOptions((los) => {
            return { ...los, [l.listId || l.dataType]: vals };
          });
        });
      } catch (error) {
        showErrorMessage({ error });
      }
    };

    const loadEntity = async () => {
      try {
        const id = queryParams.get("id");
        if (!id) {
          throw Error(t("messages.record.invalidEntityId"));
        }
        setEntityId(id);
        const result = await getEntity({ entityId: id });
        const { data = {} } = result;
        const { permissions = {}, attributes = [] } = data;
        loadListOptions(attributes);
        const canView = await hasRecordPermission(
          permissions,
          recordTypes.record.actions.view.name,
        );
        setCanView(canView);
        const canUpdate = hasRecordPermission(
          permissions,
          recordTypes.record.actions.update.name,
        );
        setCanUpdate(canUpdate);
        const canUpdateKycFields = hasRecordPermission(
          permissions,
          recordTypes.record.actions.updateKycFields.name,
        );
        setCanUpdateKycFields(canUpdateKycFields);
        const canUpdateAmlFields = hasRecordPermission(
          permissions,
          recordTypes.record.actions.updateAmlFields.name,
        );
        setCanUpdateAmlFields(canUpdateAmlFields);

        const canFinalizeAMLWitRisk = hasRecordPermission(
          permissions,
          recordTypes.record.actions.finalizeAmlScoreWithRisk.name,
        );
        setCanFinalizeAMLWithRisk(canFinalizeAMLWitRisk);

        setEntity(data);
      } catch (error) {
        if (!loadInBackground) {
          showErrorMessage({ error });
        }
      }
      setIsLoaded(true);
      setLoadInBackground(false);
      setLoad(false);
    };

    const { path } = match;
    const base =
      path === "/viewRecord"
        ? "/glc/records/viewRecord"
        : path === "/viewAuditedRecord"
        ? "/glc/admin/viewAuditedRecord"
        : "/glc/checks/viewCheck";
    setUrlBase(base);
    if (load) {
      if (!loadInBackground) {
        setIsLoaded(false);
      }
      loadEntity();
    }
  }, [load]);

  useEffect(() => {
    if (entity) {
      const { attributes = [] } = entity || {};
      const changes = getDependentAttributeChanges(attributes, listOptions);
      handleDependentValues(changes, attributes);
      setSectionAttributes();
    }
  }, [entity, listOptions]);

  const reloadEntity = async (withoutsaving, loadInBackground) => {
    if (!withoutsaving) {
      //FIX THIS - should not save when no attribute is changed
      await saveRecord();
    }
    setLoadInBackground(loadInBackground);
    setLoad(true);
  };

  const handleOnChange = (attrId, newValue) => {
    const { attributes = [] } = entity;
    const attr = attributes.find((a) => a.id === attrId);
    if (attr) {
      attr.user_value = newValue;
    }
    const changes = getDependentAttributeChanges(
      attributes,
      listOptions,
      attrId,
    );
    const changedIds = [attrId, ...handleDependentValues(changes, attributes)];
    setChangedAttrIds([
      ...changedAttrIds,
      ...changedIds.filter((id) => changedAttrIds.indexOf(id) < 0),
    ]);
    setSectionAttributes();
  };

  const resetAttribute = (attrId, oldValue) => {
    const { attributes = [] } = entity;
    const attr = attributes.find((a) => a.id === attrId);
    if (attr) {
      attr.user_value = oldValue;
    }
    const changes = getDependentAttributeChanges(
      attributes,
      listOptions,
      attrId,
    );
    handleDependentValues(changes, attributes);
    const index = changedAttrIds.indexOf(attrId);
    if (index !== -1) {
      changedAttrIds.splice(index, 1);
    }
    setChangedAttrIds([...changedAttrIds]);
    setSectionAttributes();
  };

  const handleOnUnitChange = (attrId, newUnitVal) => {
    const { attributes = [] } = entity;
    const attr = attributes.find((a) => a.id === attrId);
    if (attr) {
      attr.user_unit = newUnitVal;
    }
    if (changedAttrIds.indexOf(attrId) < 0) {
      setChangedAttrIds([...changedAttrIds, attrId]);
    }
    const changes = getDependentAttributeChanges(
      attributes,
      listOptions,
      attrId,
    );
    const changedIds = [attrId, ...handleDependentValues(changes, attributes)];
    setChangedAttrIds([
      ...changedAttrIds,
      ...changedIds.filter((id) => changedAttrIds.indexOf(id) < 0),
    ]);
    setSectionAttributes();
  };

  const handleDependentValues = (changes, attributes) => {
    const changedIds = [];
    changes.forEach((c) => {
      attributes
        .filter((a) => c.attributeIds.indexOf(a.id) > -1)
        .forEach((a) => {
          if (c.mandatoryKyc !== undefined) {
            a.mandatory_kyc = c.mandatoryKyc;
          }
          if (c.mandatoryAml !== undefined) {
            a.mandatory_aml = c.mandatoryAml;
          }
          if (c.visible !== undefined) {
            a.visible = c.visible;
            if (!c.visible && a.user_value) {
              a.user_value = "";
              changedIds.push(a.id);
            }
          }
          if (c.userValue !== undefined && a.user_value !== c.userValue) {
            a.user_value = c.userValue;
            changedIds.push(a.id);
          }
        });
    });
    return changedIds;
  };

  const setSectionAttributes = () => {
    const basicAttributes = getSectionAttributes("BASIC INFO", canUpdate, true);
    const financialAttributes = getSectionAttributes(
      "FINANCIALS",
      canUpdate,
      false,
    );
    const kycAttributes = getSectionAttributes(
      "KYC",
      canUpdate && canUpdateKycFields,
      true,
    );
    const amlAttributes = getSectionAttributes(
      "AML",
      canUpdate && canUpdateAmlFields,
      true,
    );
    setBasicAttributes(basicAttributes);
    setFinancialAttributes(financialAttributes);
    setKycAttributes(kycAttributes);
    setAmlAttributes(amlAttributes);
  };

  const getSectionAttributes = (section, canUpdate, bySubsection) => {
    const { attributes = [] } = entity || {};
    const sectionAttributes = attributes
      .filter((a) => a.section === section && a.visible !== false)
      .map((a) => {
        a.editable = a.editable && canUpdate;
        return a;
      });
    if (bySubsection) {
      const subSections = sectionAttributes.reduce(function (attrs, attr) {
        if (!attrs[attr["sub_section"]]) {
          attrs[attr["sub_section"]] = [];
        }
        attrs[attr["sub_section"]].push(attr);
        return attrs;
      }, {});

      for (let sc in subSections) {
        subSections[sc].sort((a, b) => {
          const ea = a.editable,
            eb = b.editable;
          return ea === eb ? 0 : ea ? 1 : -1;
        });
      }
      return subSections;
    } else {
      sectionAttributes.sort((a, b) => {
        const ea = a.editable,
          eb = b.editable;
        return ea === eb ? 0 : ea ? 1 : -1;
      });
      return sectionAttributes;
    }
  };

  const allocateCheck = async (cr) => {
    setCurrentRequest(cr);
    setOpenAllocateDialog(true);
  };

  const closeAllocation = async () => {
    setOpenAllocateDialog(false);
  };

  const submitAllocation = async () => {
    setOpenAllocateDialog(false);
    reloadEntity();
  };

  const validateMandatory = (checkType) => {
    const { attributes = [] } = entity || {};

    const invalidAttrs = attributes.filter((a) => {
      const mandatory =
        checkType === "KYC"
          ? a.mandatory_kyc
          : checkType === "AML"
          ? a.mandatory_aml
          : false;
      const value = getAttributeValue(a);
      return mandatory && (value === undefined || value === null);
    });

    if (invalidAttrs.length === 0) {
      return true;
    } else {
      return invalidAttrs.map(
        (a) => t(`labels.record.attributes.${a.id}`) || a.name,
      );
    }
  };

  const finalizeScore = async (cr) => {
    const { scores } = entity;
    const result = validateMandatory(cr.check_type);
    if (result === true) {
      if (changedAttrIds.length > 0) {
        showMessage({
          title: t("messages.titles.info"),
          message: t("messages.check.finalization.unsavedChanges"),
        });
        return;
      }
      const checkScores = scores.reduce((o, v) => {
        o[v.check_type] = v;
        return o;
      }, {});
      setScoreData(checkScores);
      setCurrentRequest(cr);
      setOpenFinalizeDialog(true);
    } else {
      showErrorMessage({
        message: t("messages.check.finalization.mandatoryFields", {
          fieldNames: result.join("\n"),
        }),
      });
    }
  };

  const closeFinalizing = (status) => {
    setOpenFinalizeDialog(false);
    setCurrentRequest(null);
    if (status !== "cancel") {
      reloadEntity();
    }
  };

  const approveScore = async (cr) => {
    const { scores } = entity;
    const checkScores = scores.reduce((o, v) => {
      o[v.check_type] = v;
      return o;
    }, {});
    await setScoreData(checkScores);
    await setCurrentRequest(cr);
    setOpenApproveDialog(true);
  };

  const closeApproving = (status) => {
    setOpenApproveDialog(false);
    setCurrentRequest(null);
    if (status !== "cancel") {
      reloadEntity();
    }
  };

  const showBlockDialog = async () => {
    setOpenBlockDialog(true);
  };

  const closeBlockDialog = async () => {
    setOpenBlockDialog(false);
  };

  const viewScoreComments = async () => {
    setOpenScoreComments(true);
  };

  const closeScoreComments = async () => {
    setOpenScoreComments(false);
  };

  const updateEntityConfidentiality = async (confidentiality) => {
    setSaving(true);
    try {
      await updateConfidentiality({
        entityId,
        confidential: confidentiality,
      });
      reloadEntity();
      showMessage({ message: t("messages.record.recordUpdated") });
    } catch (error) {
      showErrorMessage({ error });
    }
    setSaving(false);
  };

  const updateRecord = async () => {
    const r = await saveRecord();
    if (r) {
      setLoad(true);
      showMessage({ message: t("messages.record.recordUpdated") });
    }
  };

  const saveRecord = async (action) => {
    if (changedAttrIds.length === 0) {
      return false;
    }
    try {
      setSaving(true);
      const { attributes = [] } = entity;
      const changedAttributes = attributes
        .filter((a) => changedAttrIds.indexOf(a.id) > -1)
        .map((a) => {
          return {
            newValue: a.user_value,
            id: a.id,
            newUnit: a.user_unit,
          };
        });

      await updateEntity({ entityId, attributes: changedAttributes, action });
      setChangedAttrIds([]);
      setSaving(false);
      return true;
    } catch (error) {
      showErrorMessage({ error });
      setSaving(false);
      return false;
    }
  };

  const exportCallback = async () => {
    setShowExportOptions(true);
  };

  const handleExportOption = async (exportType) => {
    setShowExportOptions(false);
    try {
      showLoadingMessage({
        title: t("messages.titles.export"),
        message: t("messages.common.pleaseWait"),
      });
      await exportEntity(entityId, exportType);
      showMessage({ message: t("messages.record.exportCompleted") });
    } catch (error) {
      showErrorMessage({ error });
    }
  };

  const handleReferAmlToDealTeam = async () => {
    showConfirmation({
      message: t("labels.record.menu.referralConfirmationDialog.suspendReview"),
      onPressYes: async (event) => {
        try {
          setSaving(true);
          await referToDealTeam({ entityId });
          showMessage({
            message: t(
              "labels.record.menu.referralConfirmationDialog.reviewSuspended",
            ),
          });
          setSaving(false);
          reloadEntity();
        } catch (error) {
          showErrorMessage({ error });
          setSaving(false);
        }
      },
    });
  };

  const handleReferBackAmlFromDealTeam = async () => {
    showConfirmation({
      message: t("labels.record.menu.referralConfirmationDialog.resumeReview"),
      onPressYes: async (event) => {
        try {
          setSaving(true);
          await referBackFromDealTeam({ entityId });
          showMessage({
            message: t(
              "labels.record.menu.referralConfirmationDialog.reviewResumed",
            ),
          });
          setSaving(false);
          reloadEntity();
        } catch (error) {
          showErrorMessage({ error });
          setSaving(false);
        }
      },
    });
  };

  const createAMLCheck = () => {
    const { confidentiality, checkRequests } = entity;
    const kycCr = checkRequests.find((cr) => cr.check_type === "KYC");

    showConfirmation({
      message: t("messages.check.initiation.createAmlConfirm"),
      onPressYes: async (event) => {
        try {
          setSaving(true);
          await createCheckRequest({
            checkType: "AML",
            entityId: entityId,
            confidential: confidentiality,
            countryId: kycCr.country_id,
          });
          showMessage({
            message: t("messages.check.initiation.checkCreated"),
          });
          setSaving(false);
          reloadEntity();
        } catch (error) {
          showErrorMessage({ error });
          setSaving(false);
        }
      },
    });
  };

  const handelCancelCheck = () => {
    setOpenCancelCheckDialog(true);
  };

  const cancelCheckDialogClose = async (submtted) => {
    if (submtted) {
      reloadEntity();
    }
    setOpenCancelCheckDialog(false);
  };

  const openAdditionalSourcesDialog = () => {
    setShowAdditionalSources(true);
  };

  const closeAdditionalSourcesDialog = (reload) => {
    setShowAdditionalSources(false);
    if (reload) {
      reloadEntity();
    }
  };

  const renderLoadingIndicator = () => {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "70vh",
        }}
      >
        <LoadingIndicator />
      </div>
    );
  };

  if (isLoaded && !canView) {
    return <NoAccessScreen />;
  }

  if (!isLoaded) {
    return renderLoadingIndicator();
  }

  const { user } = props;

  return (
    <Router basename={urlBase}>
      <RecordSideBar
        entity={entity}
        entityId={entityId}
        hasRecordPermission={hasRecordPermission}
      />
      <PageHeader
        entity={entity}
        hasRecordPermission={hasRecordPermission}
        hasPermission={hasPermission}
        allocateCheck={allocateCheck}
        finalizeScore={finalizeScore}
        approveScore={approveScore}
        updateRecord={updateRecord}
        updateEntityConfidentiality={updateEntityConfidentiality}
        sendBlockRequest={showBlockDialog}
        exportCallback={exportCallback}
        viewScoreComments={viewScoreComments}
        isDirty={changedAttrIds.length > 0}
        handelCancelCheck={handelCancelCheck}
        createAMLCheck={createAMLCheck}
        onAdditionalSourcesClick={openAdditionalSourcesDialog}
        handleReferAmlToDealTeam={handleReferAmlToDealTeam}
        handleReferBackAmlFromDealTeam={handleReferBackAmlFromDealTeam}
      />
      {openAllocateDialog && (
        <AllocateRequest
          isOpen={openAllocateDialog}
          request={currentRequest}
          user={user}
          onClose={closeAllocation}
          onSubmit={submitAllocation}
        ></AllocateRequest>
      )}
      {openFinalizeDialog && (
        <FinalizeScore
          open={openFinalizeDialog}
          scoreData={scoreData}
          checkRequest={currentRequest}
          onClose={closeFinalizing}
          canFinalizeAMLWithRisk={canFinalizeAMLWithRisk}
          props={props}
        ></FinalizeScore>
      )}
      {openApproveDialog && (
        <ApproveRequest
          open={openApproveDialog}
          scoreData={scoreData}
          checkRequest={currentRequest}
          entity={entity}
          onClose={closeApproving}
          props={props}
        ></ApproveRequest>
      )}
      {openBlockDialog && (
        <BlockUnblockRequest
          open={openBlockDialog}
          entity={entity}
          onSubmit={closeBlockDialog}
          onClose={closeBlockDialog}
        />
      )}
      {openScoreComments && (
        <Comments
          open={openScoreComments}
          entity={entity}
          user={user}
          hasRecordPermission={hasRecordPermission}
          hasPermission={hasPermission}
          onClose={closeScoreComments}
        />
      )}

      {openCancelCheckDialog && (
        <CancelChecks
          isOpen={openCancelCheckDialog}
          hasPermission={hasPermission}
          hasRecordPermission={hasRecordPermission}
          entityId={entityId}
          onClose={cancelCheckDialogClose}
        ></CancelChecks>
      )}

      {showAdditionalSources && (
        <AugmentRecordDialog
          isOpen={showAdditionalSources}
          onClose={closeAdditionalSourcesDialog}
          entity={entity}
          listOptions={listOptions}
          onAttributeChange={handleOnChange}
          resetAttribute={resetAttribute}
          onUnitChange={handleOnUnitChange}
          saveRecord={saveRecord}
          reloadEntity={reloadEntity}
          user={user}
          hasRecordPermission={hasRecordPermission}
          hasPermission={hasPermission}
        />
      )}

      {showExportOptions && (
        <ExportSelectorDialog
          isOpen={showExportOptions}
          onClose={() => setShowExportOptions(false)}
          onSubmit={handleExportOption}
        />
      )}

      {saving && renderLoadingIndicator()}
      <Switch>
        <Route exact path="/">
          <Redirect to={`/basicinfo?id=${entityId}`} />
        </Route>
        <Route exact path="/basicinfo">
          {!saving && (
            <BasicInfo
              entity={entity}
              handleOnChange={handleOnChange}
              handleOnUnitChange={handleOnUnitChange}
              onContactUpdate={reloadEntity}
              hasRecordPermission={hasRecordPermission}
              basicAttributes={basicAttributes}
              financialAttributes={financialAttributes}
              listOptions={listOptions}
            />
          )}
        </Route>
        <Route exact path="/kyc">
          {!saving && (
            <KYC
              entity={entity}
              handleOnChange={handleOnChange}
              handleOnUnitChange={handleOnUnitChange}
              hasRecordPermission={hasRecordPermission}
              listOptions={listOptions}
              kycAttributes={kycAttributes}
            />
          )}
        </Route>
        <Route exact path="/financials">
          {!saving && (
            <Financial
              entity={entity}
              handleOnChange={handleOnChange}
              handleOnUnitChange={handleOnUnitChange}
              hasRecordPermission={hasRecordPermission}
              listOptions={listOptions}
              financialAttributes={financialAttributes}
              reloadEntity={reloadEntity}
            />
          )}
        </Route>
        <Route exact path="/aml">
          {!saving && (
            <AML
              entity={entity}
              handleOnChange={handleOnChange}
              handleOnUnitChange={handleOnUnitChange}
              onAssociationUpdate={reloadEntity}
              reloadEntity={reloadEntity}
              user={user}
              hasRecordPermission={hasRecordPermission}
              hasPermission={hasPermission}
              amlAttributes={amlAttributes}
              listOptions={listOptions}
            />
          )}
        </Route>
        <Route exact path="/relatedEntities">
          {!saving && (
            <RelatedEntities
              entity={entity}
              user={user}
              hasRecordPermission={hasRecordPermission}
              hasPermission={hasPermission}
            />
          )}
        </Route>
        <Route exact path="/orgStructure">
          {!saving && (
            <OrgStructure
              entity={entity}
              user={user}
              hasRecordPermission={hasRecordPermission}
              hasPermission={hasPermission}
            />
          )}
        </Route>
        <Route exact path="/audit">
          {!saving && <Audit entityId={entityId} />}
        </Route>
        <Route exact path="/documentRequests">
          <DocumentRequests
            entity={entity}
            reloadEntity={reloadEntity}
            hasRecordPermission={hasRecordPermission}
          />
        </Route>
      </Switch>
    </Router>
  );
};

export default withUserDetails(
  withOktaAuth(withApollo(withStyles(styles, { withTheme: true })(RecordView))),
);
